"use client";

import { size, theme } from "@/config/theme";
import { SCButton, SCContainerContent } from "@/utils/styledComponents";
import { useTranslations } from "next-intl";
import styled from "styled-components";
import imageAboutUs from "/public/media/images/image-aboutus.png";
import Image from "next/image";
import { breakpoints } from "@/utils/breakpoint";
import { useRouter } from "next/navigation";
import { useRecoilValue } from "recoil";
import { appLocale } from "@/recoil/appState";
import { canonicalPath } from "@/utils/canonicalPath";

const AboutUsBanner = () => {
    const t = useTranslations("AboutUsBanner");
    const router = useRouter();
    const localeAppState = useRecoilValue(appLocale);

    return (
        <SCAboutUsBanner>
            <SCAboutUsBannerContent>
                <div className="text-container">
                    <div className="text-small">{t("top-text")}</div>
                    <h1 className="text-xlarge">{t("main-text")}</h1>
                    <div className="text-medium">{t("bottom-text")}</div>
                    <div>
                        <SCBookTrialButton
                            onClick={() => {
                                router.push(
                                    canonicalPath(
                                        `/${localeAppState}/book-trial`
                                    )
                                );
                            }}
                        >
                            {t("Book Free Trial")}
                        </SCBookTrialButton>
                    </div>
                </div>
                <div className="image-container">
                    <Image
                        src={imageAboutUs}
                        alt="Swimming Classes"
                        priority
                        fetchPriority="high"
                        sizes="(max-width: 768px) 100vw, 50vw"
                    />
                </div>
            </SCAboutUsBannerContent>
        </SCAboutUsBanner>
    );
};

export default AboutUsBanner;

const SCAboutUsBanner = styled.div`
    background-color: ${theme["color-light-yellow"]};
    display: flex;
    flex-direction: row;
    justify-content: center;
    padding: ${theme["spacing-lxl"]} 0;
    margin-top: -80px;
    padding-top: 80px;
`;

const SCAboutUsBannerContent = styled(SCContainerContent)`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;

    // Mobile view
    ${breakpoints(
        "flex-direction",
        "",
        [{ [size.px.sxs]: "column" }],
        "max-width"
    )};

    > div {
        width: 40%;

        // Mobile view
        ${breakpoints("width", "", [{ [size.px.sxs]: "100%" }], "max-width")};
    }

    .text-container {
        .text-small {
            font-size: ${theme["fontsize-s"]};
            color: ${theme["color-blue"]};
        }

        .text-medium {
            font-size: ${theme["fontsize-m"]};
            color: ${theme["color-grey"]};
        }

        .text-xlarge {
            font-size: ${theme["fontsize-xl"]};

            // Mobile view
            ${breakpoints(
                "font-size",
                "px",
                [{ [size.px.xs]: "35" }],
                "max-width"
            )};
        }

        > div,
        > h1 {
            margin-bottom: ${theme["spacing-l"]};

            &:last-child {
                // Non-mobile view
                ${breakpoints(
                    "margin-bottom",
                    "",
                    [{ [(parseInt(size.px.sxs) + 1).toString()]: "0" }],
                    "min-width"
                )};
            }
        }
    }

    .image-container {
        display: flex;
        align-items: center;
        justify-content: center;

        img {
            width: 400px;
            min-width: 300px;
            height: auto;

            // Mobile view
            ${breakpoints(
                "width",
                "",
                [{ [size.px.sxs]: "60%" }],
                "max-width"
            )};
        }
    }
`;

const SCBookTrialButton = styled(SCButton)`
    background-color: ${theme["color-white"]};
    color: ${theme["color-blue"]};
    margin-right: ${theme["spacing-ml"]};
    border: 1px solid ${theme["color-blue"]};
`;
